import React, { useEffect, useRef, useState } from 'react';
import ApiService from '../services/api';

/**
 * Renders Google Identity Services button when Client ID is configured.
 */
const GoogleSignInButton = ({ onCredential, disabled = false, text = 'signin_with' }) => {
  const buttonRef = useRef(null);
  const [clientId, setClientId] = useState('');
  const [ready, setReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const callbackRef = useRef(onCredential);

  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const res = await ApiService.getGoogleClientId();
        if (cancelled) return;
        if (!res.clientId) {
          setUnavailable(true);
          return;
        }
        setClientId(res.clientId);

        if (!window.google?.accounts?.id) {
          await new Promise((resolve, reject) => {
            const existing = document.querySelector('script[data-google-gsi]');
            if (existing) {
              existing.addEventListener('load', resolve);
              existing.addEventListener('error', reject);
              return;
            }
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.dataset.googleGsi = '1';
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }

        if (cancelled || !window.google?.accounts?.id) return;

        window.google.accounts.id.initialize({
          client_id: res.clientId,
          callback: (response) => {
            if (callbackRef.current) {
              callbackRef.current(response.credential);
            }
          }
        });
        setReady(true);
      } catch (err) {
        if (!cancelled) setUnavailable(true);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !buttonRef.current || !window.google?.accounts?.id || disabled) return;
    buttonRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: 'outline',
      size: 'large',
      width: 320,
      text,
      shape: 'rectangular'
    });
  }, [ready, disabled, text, clientId]);

  if (unavailable) {
    return (
      <p className="auth-google-hint">
        Google Sign-In will appear here once the Client ID is added in <code>api/google-config.php</code>.
      </p>
    );
  }

  return (
    <div className={`google-signin-wrap ${disabled ? 'is-disabled' : ''}`}>
      <div ref={buttonRef} />
    </div>
  );
};

export default GoogleSignInButton;
